import { useEffect } from "react";

type Props = {
    onOpen: () => void;
    questionName?: string;
    buttonId?: string;
};

const MAP_ICON_ID = "map-icon-injector-btn";

function createMapButton(onOpen: () => void): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.title = "Open map";
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`;

    Object.assign(btn.style, {
        position: "absolute",
        right: "32px",
        top: "50%",
        transform: "translateY(-50%)",
        background: "none",
        border: "none",
        padding: "2px",
        margin: "0",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#909090",
        zIndex: "1",
        lineHeight: "0",
    });

    btn.addEventListener("mouseenter", () => {
        btn.style.color = "#5271FF";
    });
    btn.addEventListener("mouseleave", () => {
        btn.style.color = "#909090";
    });

    btn.addEventListener("click", (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        onOpen();
    });

    return btn;
}

function injectIcon(onOpen: () => void, questionName: string, buttonId: string): boolean {
    if (document.getElementById(buttonId)) {
        return true;
    }

    const questionContainer = document.querySelector(`[data-name="${questionName}"]`);
    if (!questionContainer) {
        return false;
    }

    // SurveyJS tagbox renders an .sd-formbox__input wrapper around the tags and input
    const inputWrapper = questionContainer.querySelector(".sd-formbox__input") as HTMLElement | null;
    if (!inputWrapper) {
        return false;
    }

    // Ensure the wrapper is positioned so the absolute button is placed relative to it
    const currentPosition = window.getComputedStyle(inputWrapper).position;
    if (currentPosition === "static") {
        inputWrapper.style.position = "relative";
    }

    const btn = createMapButton(onOpen);
    btn.id = buttonId;
    inputWrapper.appendChild(btn);
    return true;
}

export default function MapIconInjector({ onOpen, questionName = "locations", buttonId = MAP_ICON_ID }: Props) {
    useEffect(() => {
        if (injectIcon(onOpen, questionName, buttonId)) {
            return () => {
                document.getElementById(buttonId)?.remove();
            };
        }

        const observer = new MutationObserver(() => {
            if (injectIcon(onOpen, questionName, buttonId)) {
                observer.disconnect();
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
            document.getElementById(buttonId)?.remove();
        };
    }, [onOpen, questionName, buttonId]);

    return null;
}
